import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  DetailsRow,
  Dialog,
  DialogType,
  IColumn,
  ICommandBarItemProps,
  IDetailsRowProps,
  Label,
  ProgressIndicator,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Text,
} from '@fluentui/react';
import { Link } from '@micro-frontend-react/employee-experience/lib/Link';
import styled from 'styled-components';
import * as React from 'react';
import * as PopupText from '../../../Common/CommonModels/PopupText';
import { DataOwnerRecord } from '../../../Common/DataOwnerModels/DataOwnerRecord';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import { useGetAllDataOwners } from '../../../Hooks/DataOwner/useGetAllDataOwners';
import { AddDataOwnerPanel } from './AddDataOwnerPanel';

type DataOwnerPageProps = {
  addNew: boolean;
};

export function DataOwnerPage(props: DataOwnerPageProps): React.ReactElement {
  const { addNew } = props;
  const [addDataOwnerPanelOpen, setAddDataOwnerPanelOpen] = React.useState<boolean>(addNew);
  const [showConfirmationPopup, setShowConfirmationPopup] = React.useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);

  const shouldReload = showSuccessMessage && addDataOwnerPanelOpen == false;
  const [dataOwnerList, dataOwnerListLoading] = useGetAllDataOwners(shouldReload);

  const dataOwnerTableColumns: IColumn[] = [
    {
      key: 'name',
      name: 'DataOwner Name',
      minWidth: 250,
      maxWidth: 250,
    },
    {
      key: 'id',
      name: 'DataOwner ID',
      minWidth: 300,
      maxWidth: 350,
    },
    {
      key: 'serviceTreeName',
      name: 'Service Tree Name',
      minWidth: 250,
      maxWidth: 250,
    },
    {
      key: 'serviceTreeID',
      name: 'Service Tree Id',
      minWidth: 350,
      maxWidth: 350,
    },
    {
      key: 'securityGroup',
      name: 'Security Group',
      minWidth: 200,
      maxWidth: 200,
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addDataOwner',
      text: 'Add Data Owner',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        setAddDataOwnerPanelOpen(true);
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];

  const onRenderItemColumn = (item: DataOwnerRecord, index: number, column: IColumn): React.ReactNode => {
    if (column.key == 'name') {
      return (
        <div aria-label={`Column header: ${column.key}. Cell content: ${item.name}`}>
          <StyledLink
            title="Go to Data Owner Management"
            to={`/dataOwners/${item.id}`}
            usageEvent={{
              feature: 'HRDP',
              subFeature: 'HRDP.DataOwners',
            }}
          >
            {item.name}
          </StyledLink>
        </div>
      );
    }
    return <Text>{item[column.key as keyof DataOwnerRecord]}</Text>;
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  const dismissAddDataOwnerPanel = () => {
    setAddDataOwnerPanelOpen(false);
  };

  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  return (
    <div data-testid="data-owner-page">
      <title>DataOwners</title>

      <Styled.PortalPageHeadingSection>
        <Styled.PortalPageTitleText>Data Owners</Styled.PortalPageTitleText>
      </Styled.PortalPageHeadingSection>

      <Styled.ButtonsTableSection>
        <Styled.PortalPageCommandBar items={commandBarItems} ariaLabel="Command Bar" />

        <ShimmeredDetailsList
          items={dataOwnerList || []}
          enableShimmer={dataOwnerListLoading}
          columns={dataOwnerTableColumns}
          onRenderItemColumn={onRenderItemColumn}
          onRenderRow={onRenderRow}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          checkboxVisibility={CheckboxVisibility.hidden}
          ariaLabelForShimmer="Data Owners Table is being fetched"
          ariaLabelForGrid="Data Owners Table"
        />
      </Styled.ButtonsTableSection>

      {addDataOwnerPanelOpen && (
        <AddDataOwnerPanel
          isOpen={addDataOwnerPanelOpen}
          dismissPanel={dismissAddDataOwnerPanel}
          setShowConfirmationPopup={setShowConfirmationPopup}
          setShowSuccessMessage={setShowSuccessMessage}
          setShowErrorMessage={setShowErrorMessage}
        />
      )}

      <Dialog
        hidden={!showConfirmationPopup}
        onDismiss={closeConfirmationPopup}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        modalProps={{
          isBlocking: showSuccessMessage || showErrorMessage ? false : true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Stack>
          <Label styles={{ root: { fontSize: 16 } }}>
            {!showSuccessMessage && !showErrorMessage && PopupText.submitRequestInProgress}
            {showSuccessMessage && PopupText.submitRequestSuccess}
            {showErrorMessage && PopupText.userError}
          </Label>
          {!showSuccessMessage && !showErrorMessage && <ProgressIndicator></ProgressIndicator>}
          <Text styles={{ root: { fontSize: 12, paddingTop: 15 } }}>
            {!showSuccessMessage && !showErrorMessage && ''}
            {showSuccessMessage && PopupText.requestSuccessDescription}
            {showErrorMessage && PopupText.userErrorDescription}
          </Text>
        </Stack>
      </Dialog>
    </div>
  );
}

const StyledLink = styled(Link)`
  font-size: 14px;
  line-height: 20px;
`;
